import { useEffect, useMemo, useRef, useState } from "react";
import {
  alcaNoPonto, corteInicial, emFracao, moverAlca, type Alca, type Retangulo,
} from "@nhomi/core";
import type { Recorte } from "@/lib/image";

/* ============================================================
   Cortar uma foto — corte livre

   O jeito de todo app (pedido com vídeo do WhatsApp): a foto INTEIRA
   na tela e um retângulo por cima, com alças nos quatro cantos e nos
   quatro lados. Arrastar uma alça muda o tamanho; arrastar por dentro
   move. Fora do retângulo fica escuro, dentro tem a grade de terços. Um
   botão gira a foto de 90 em 90 graus.

   Antes eram três formatos prontos (quadrada, 4:5, original) com a foto
   se mexendo por baixo de um quadro fixo — não dava para escolher o
   corte, só onde encaixar um formato.

   A conta das alças é do core (`corte.ts`), a mesma do app. O que sai
   daqui é só o retângulo em frações da foto girada; quem corta de
   verdade é `lib/image`, na mesma passada que já reduz a foto.

   `quadrado` trava a proporção em 1:1 (foto de perfil): os cantos
   mantêm o quadrado.
   ============================================================ */

type Giro = 0 | 90 | 180 | 270;

export function Recortador(
  { foto, quadrado = false, titulo, onPronto, onCancelar }: {
    /** A foto original (os bytes que a pessoa escolheu). */
    foto: Blob;
    quadrado?: boolean;
    titulo: string;
    onPronto: (recorte: Recorte) => void;
    onCancelar: () => void;
  },
) {
  const url = useMemo(() => URL.createObjectURL(foto), [foto]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [giro, setGiro] = useState<Giro>(0);
  const palco = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState({ w: 320, h: 420 });

  useEffect(() => {
    const medir = () => {
      const el = palco.current;
      if (el) setArea({ w: el.clientWidth - 32, h: el.clientHeight - 32 });
    };
    medir();
    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
  }, []);

  // A foto girada cabe INTEIRA na área (contain).
  const deitada = giro === 90 || giro === 270;
  const nw = natural ? (deitada ? natural.h : natural.w) : 1;
  const nh = natural ? (deitada ? natural.w : natural.h) : 1;
  const escala = Math.min(area.w / nw, area.h / nh);
  const caixa = { w: Math.max(1, nw * escala), h: Math.max(1, nh * escala) };
  const razao = quadrado ? 1 : undefined;

  const [corte, setCorte] = useState<Retangulo>({ x: 0, y: 0, w: 1, h: 1 });
  // Foto nova, giro novo ou tela nova: recomeça com a foto toda (ou o maior quadrado).
  useEffect(() => {
    if (natural) setCorte(corteInicial(caixa, razao));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [natural, giro, area.w, area.h]);

  /* ---------- gesto ---------- */
  const gesto = useRef<{ id: number; alca: Alca; x: number; y: number; inicio: Retangulo } | null>(null);

  function ponto(e: React.PointerEvent) {
    const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return { x: e.clientX - box.left, y: e.clientY - box.top };
  }
  function apertar(e: React.PointerEvent) {
    if (gesto.current) return;
    const p = ponto(e);
    const alca = alcaNoPonto(corte, p.x, p.y, e.pointerType === "mouse" ? 14 : 28);
    if (!alca) return;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    gesto.current = { id: e.pointerId, alca, x: p.x, y: p.y, inicio: corte };
  }
  function mover(e: React.PointerEvent) {
    const g = gesto.current;
    if (!g || g.id !== e.pointerId) return;
    const p = ponto(e);
    setCorte(moverAlca(g.inicio, g.alca, p.x - g.x, p.y - g.y, caixa, 48, razao));
  }
  function soltar(e: React.PointerEvent) {
    if (gesto.current?.id === e.pointerId) gesto.current = null;
  }

  function pronto() {
    if (!natural) return;
    onPronto({ ...emFracao(corte, caixa), giro });
  }

  const alcas: Alca[] = ["tl", "t", "tr", "r", "br", "b", "bl", "l"];

  /* A imagem é desenhada girada por CSS dentro da caixa: antes de girar
     ela tem as medidas "em pé" (trocadas quando deitada). */
  const largImg = deitada ? caixa.h : caixa.w;
  const altImg = deitada ? caixa.w : caixa.h;

  return (
    <div className="recorte" role="dialog" aria-label={titulo}>
      <div className="recorte-topo">
        <button className="recorte-txt" onClick={onCancelar}>Cancelar</button>
        <span className="recorte-titulo">{titulo}</span>
        <button className="recorte-txt forte" onClick={pronto} disabled={!natural}>OK</button>
      </div>

      <div className="recorte-palco" ref={palco}>
        <div className="recorte-caixa" style={{ width: caixa.w, height: caixa.h }}
          onPointerDown={apertar} onPointerMove={mover} onPointerUp={soltar} onPointerCancel={soltar}>
          <img src={url} alt="" draggable={false}
            onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            style={{
              width: largImg, height: altImg,
              left: (caixa.w - largImg) / 2, top: (caixa.h - altImg) / 2,
              transform: `rotate(${giro}deg)`,
            }} />
          {natural && (
            <div className="recorte-sel" data-testid="corte"
              style={{ left: corte.x, top: corte.y, width: corte.w, height: corte.h }}>
              <div className="recorte-grade" aria-hidden="true" />
              {alcas.map((a) => <span key={a} className={`recorte-alca ${a}`} aria-hidden="true" />)}
            </div>
          )}
        </div>
      </div>

      <div className="recorte-base">
        <button className="recorte-girar" aria-label="Girar"
          onClick={() => setGiro((g) => ((g + 90) % 360) as Giro)}>⟳</button>
        <p className="recorte-dica">
          {quadrado ? "Arraste os cantos ou o quadro" : "Arraste os cantos e os lados, ou o quadro inteiro"}
        </p>
      </div>
    </div>
  );
}
